import React, { useState } from 'react';
import { Play, ArrowUpRight, Apple, Youtube } from 'lucide-react';
import { RELEASES, ALL_TRACKS } from '../mock';
import Reveal from './Reveal';
import { SpotifyIcon } from './icons';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';

const PlatformLinks = ({ track }) => (
  <div className="mt-3 flex items-center gap-4">
    <a
      href={track.spotify || 'https://open.spotify.com'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 text-[9px] tracking-[0.15em] text-neutral-500 transition-colors duration-300 hover:text-gold"
    >
      <SpotifyIcon size={11} /> Spotify
    </a>
    <a
      href={track.apple || 'https://music.apple.com'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 text-[9px] tracking-[0.15em] text-neutral-500 transition-colors duration-300 hover:text-gold"
    >
      <Apple size={11} /> Apple Music
    </a>
    <a
      href={track.youtube || 'https://youtube.com'}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-1.5 text-[9px] tracking-[0.15em] text-neutral-500 transition-colors duration-300 hover:text-gold"
    >
      <Youtube size={11} /> YouTube
    </a>
  </div>
);

const ReleaseCard = ({ track, onPlay, delay }) => (
  <Reveal delay={delay}>
    <div className="group">
      <button
        onClick={() => onPlay(track)}
        className="relative block w-full overflow-hidden text-left"
        aria-label={`Play ${track.title}`}
      >
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={track.cover}
            alt={`${track.title} — ${track.artist}`}
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/60 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-400 group-hover:opacity-100">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-gold text-ink transition-transform duration-300 group-hover:scale-110">
              <Play size={18} fill="currentColor" />
            </span>
          </div>
        </div>
      </button>
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => onPlay(track)}
          aria-label={`Play ${track.title}`}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-white/25 text-white transition-colors duration-300 hover:border-gold hover:bg-gold hover:text-ink"
        >
          <Play size={11} fill="currentColor" />
        </button>
        <div>
          <p className="text-xs tracking-[0.1em] text-neutral-200">
            {track.title.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase())}
          </p>
          <p className="text-[10px] tracking-[0.08em] text-neutral-500">{track.artist}</p>
        </div>
      </div>
      <PlatformLinks track={track} />
    </div>
  </Reveal>
);

const Music = ({ onPlay }) => {
  const [allOpen, setAllOpen] = useState(false);

  return (
    <section id="music" className="bg-ink py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-14 flex flex-wrap items-end justify-between gap-6">
          <div>
            <Reveal>
              <p className="mb-5 text-[10px] uppercase tracking-[0.5em] text-gold">Music</p>
            </Reveal>
            <Reveal delay={120}>
              <h2 className="font-serif text-4xl font-light leading-tight tracking-[0.04em] text-white md:text-6xl">
                LATEST
                <br />
                RELEASES
              </h2>
            </Reveal>
          </div>
          <Reveal delay={200}>
            <button
              onClick={() => setAllOpen(true)}
              className="group inline-flex items-center gap-2 pb-2 text-[10px] tracking-[0.3em] text-neutral-300 transition-colors duration-300 hover:text-gold"
            >
              VIEW ALL MUSIC
              <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 gap-x-6 gap-y-12 sm:grid-cols-2 xl:grid-cols-4">
          {RELEASES.map((track, i) => (
            <ReleaseCard key={track.id} track={track} onPlay={onPlay} delay={i * 110} />
          ))}
        </div>
      </div>

      {/* All music dialog */}
      <Dialog open={allOpen} onOpenChange={setAllOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-ink text-neutral-200 sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl font-light tracking-[0.06em] text-white">
              ALL MUSIC
            </DialogTitle>
            <DialogDescription className="text-[10px] uppercase tracking-[0.4em] text-gold">
              The complete discography
            </DialogDescription>
          </DialogHeader>
          <div className="divide-y divide-white/5 pt-2">
            {ALL_TRACKS.map((track, i) => (
              <button
                key={track.id}
                onClick={() => {
                  setAllOpen(false);
                  onPlay(track);
                }}
                className="group flex w-full items-center gap-4 py-3.5 text-left transition-colors duration-300 hover:bg-white/[0.03]"
              >
                <span className="w-7 text-right font-serif text-base italic text-neutral-600">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <img
                  src={track.cover}
                  alt=""
                  loading="lazy"
                  className="h-11 w-11 rounded-sm object-cover"
                />
                <span className="flex-1">
                  <span className="block text-xs tracking-[0.15em] text-neutral-100 transition-colors duration-300 group-hover:text-gold">
                    {track.title}
                  </span>
                  <span className="block text-[10px] tracking-[0.08em] text-neutral-500">
                    {track.artist} · {track.year}
                  </span>
                </span>
                <span className="text-[10px] tracking-[0.1em] text-neutral-500">{track.duration}</span>
                <span className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white transition-colors duration-300 group-hover:border-gold group-hover:bg-gold group-hover:text-ink">
                  <Play size={11} fill="currentColor" />
                </span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Music;
